/* -- Angular Dependencies -- */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';

/* -- 3rd party Dependencies -- */
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { XFORMModule } from './xform-compat';

/* -- Custom Services -- */
import { HttpLoaderFactory } from './core/i18n/loader';

/* -- Custom Components -- */
import { AppComponent } from './app.component';

/* -- Custom Modules -- */
import { CoreModule } from './core/core.module';
import { AnalyticsIdentityService, AuthenticationService, EnvIndicatorService } from './core/services';
import { AppRoutingModule } from './app-routing.module';
import { HeaderModule } from './modules/header/header.module';
import { ReleaseNotesSharedModule } from './modules/release-notes/release-notes-shared.module';

import { CONFIG } from './config';
import { NgxEchartsModule } from 'ngx-echarts';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MarkdownModule } from 'ngx-markdown';


@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    XFORMModule.forRoot({
      configFile: CONFIG,
      translationService: TranslateService
    }),
    MarkdownModule.forRoot(),
    NgxEchartsModule.forRoot({
      echarts: chartModule
    }),
    HeaderModule,
    ReleaseNotesSharedModule,
    FormsModule,
    AppRoutingModule // always AppRoutingModule at the end
  ],
  providers: [
    {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {appearance: 'outline'}},
    {
      provide: APP_INITIALIZER,
      useFactory: (envIndicatorService: EnvIndicatorService) => () => envIndicatorService.init(),
      deps: [EnvIndicatorService],
      multi: true
    },
    {
      // Repairs a session that is authenticated but has no stored user object. Deferred out of the
      // service constructor because AuthInterceptor injects AuthenticationService — see init()'s doc.
      provide: APP_INITIALIZER,
      useFactory: (authenticationService: AuthenticationService) => () => authenticationService.init(),
      deps: [AuthenticationService],
      multi: true
    },
    {
      // Must run before the router's first navigation so analytics identity precedes the first
      // page_view and the first *_view event. See AnalyticsIdentityService's doc comment.
      provide: APP_INITIALIZER,
      useFactory: (analyticsIdentityService: AnalyticsIdentityService) => () => analyticsIdentityService.init(),
      deps: [AnalyticsIdentityService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}

export function chartModule(): any {
  return import('echarts');
}
